// Field help for the customers screens (the list, the Add customer dialog, and the customer
// page's Usage, People, AWS accounts, Sign-in, Keys and Settings tabs). The guardrail
// interventions' former Triggered card has no topic: they are Activity → Log rows (REQ-243),
// explained in the Audit log article's "A customer's Activity". Keys are the field ids
// passed to <Field help="…">; every rendered label must have one (tests/unit/help.test.ts). Copy
// follows docs/guides/admin-guide.md.
import type { HelpTopic } from "../topic";

export const CUSTOMERS_TOPICS = {
  "customers.name": {
    title: "Name",
    body: "The customer's name as people know it. It is what every screen shows; the id stays in the background.",
    example: "“Hooli” with the id hooli. You can use up to 200 characters.",
    article: "adding-a-customer",
  },
  "customers.id": {
    title: "Id",
    body: "The customer's id. It must equal the org_id claim value its developers' tokens carry, so it is not invented here, and it cannot change later. Lowercase letters, numbers and hyphens.",
    example: "If the customer's tokens carry org_id “hooli”, the id is hooli. A second customer with the same id is refused.",
    article: "adding-a-customer",
  },
  "customers.slug": {
    title: "Slug",
    body: "A short, URL-safe handle for the customer. Lowercase letters, numbers and hyphens, up to 64 characters.",
    example: "Acme Rocket Skates might use the slug acme-rocket-skates.",
    article: "adding-a-customer",
  },
  "customers.billedThisMonth": {
    title: "Billed this month",
    body: "What the customer is billed for the current month so far, from the revenue report: AWS cost with its rate plan's markup, plus approved adjustments. The month is open, so this is a preview. “Not billed” means the customer is proxy only.",
    example: "On the 20th, $3,367.28 of AWS cost at a 30 % markup shows $4,377.47 billed so far.",
    article: "revenue-and-margin",
    section: "not-billed",
  },
  "customers.requests": {
    title: "Requests",
    body: "Calls the customer's developers made through the Nexus proxy over the last 30 days (UTC), successful or not.",
    example: "12,480 requests means 12,480 calls to Bedrock went through the customer's proxies.",
    article: "what-nexus-does",
  },
  "customers.tokens": {
    title: "Tokens",
    body: "Every token the customer used over the last 30 days: input, output, cache reads and cache writes added together.",
    example: "8.2M input + 1.1M output + 3.4M cache read + 0.3M cache write = 13M tokens.",
    article: "pricing-and-markup",
    section: "tokens-to-invoice",
  },
  "customers.errorRate": {
    title: "Error rate",
    body: "The share of the last 30 days' requests that failed (HTTP status 400 or above), whatever the cause: throttling, a bad request, or a Bedrock error.",
    example: "37 failed requests out of 12,480 is an error rate of 0.30 %.",
    article: "installations",
    section: "health",
  },
  "customers.tokensByDay": {
    title: "Tokens by day",
    body: "The customer's tokens per UTC day over the last 30 days. The table beside the chart has the same figures, with requests and the AWS list cost.",
    example: "A bar of 450k on 3 Sep means the customer used 450,000 tokens between 00:00 and 24:00 UTC that day.",
    article: "what-nexus-does",
  },
  "usage.byTeam": {
    title: "Usage by team",
    body: "The customer's requests, tokens and AWS list cost per team over the last 30 days (UTC), with a line per team's tokens each day. Usage of people in no team adds up under “No team”.",
    example: "Platform at 1.2M tokens and No team at 40k means most usage came from Platform's developers.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "usage.byUser": {
    title: "Usage by user",
    body: "The customer's requests, tokens and AWS list cost per developer over the last 30 days (UTC), with a line per person's tokens each day. A person shows by name, else email, else their sign-in id.",
    example: "Ana at 800k tokens is the customer's heaviest user this month; her email is under her name.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "customers.installationHealth": {
    title: "Installation health",
    body: "Whether each proxy installation is reporting in. Healthy: a heartbeat in the last 15 minutes. Stale: older than that. No heartbeat yet: never reported. Revoked: its secrets no longer authenticate.",
    example: "A proxy last heard from 40 minutes ago is Stale — check its stack before developers notice.",
    article: "installations",
    section: "health",
  },
  "customers.users": {
    title: "Users",
    body: "Everyone who has used the proxy for this customer. Users are created the first time a request names them (AWS sign-in, an OIDC token or an API key), so nobody needs adding by hand.",
    example: "A developer who runs Claude Code through AWS sign-in appears here after their first request, named from their Identity Center user.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "customers.teams": {
    title: "Teams",
    body: "Groups of users for the usage reports. A team comes from the team_id claim of a token, an AWS sign-in team mapping, or an admin.",
    example: "Mapping permission-set/Developers to the team “platform” puts every developer of that permission set in Platform.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "customers.team": {
    title: "Team",
    body: "The team a user's usage counts under in reports. A user without a team is shown as “No team”.",
    example: "A developer in the Platform team adds to Platform's row of the usage-by-team report.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "customers.teamId": {
    title: "Team id",
    body: "The team's id as the proxy sees it: the team_id claim value, or the id a team mapping names.",
    example: "A mapping to team id “platform” lands callers in the team whose id is platform.",
    article: "users-teams-and-sign-in",
    section: "aws-sign-in",
  },
  "customers.canReadPrompts": {
    title: "Reads prompts",
    body: "Whether the user may read stored prompt logs (the break-glass prompt-reader permission). Every read is audited.",
    example: "A security lead investigating an incident is given it for the investigation, then it is taken away.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "customers.installations": {
    title: "Proxy installations",
    body: "Each installation is one Nexus proxy stack deployed in one of the customer's AWS accounts. It authenticates to Nexus with its own secret, and its id is the audience developers use for AWS sign-in.",
    example: "Hooli runs one installation in 111111111111 (us-east-1) and another in 222222222222 (eu-west-1).",
    article: "installations",
  },
  "customers.installationName": {
    title: "Name",
    body: "A name for the installation that says where it runs. Two installations of one customer cannot share a name.",
    example: "“prod-us-east-1” or “sandbox”.",
    article: "installations",
  },
  "customers.awsAccountId": {
    title: "AWS account id",
    body: "The 12-digit id of the AWS account the proxy is deployed in. Optional here; that account is always allowed to sign in with AWS.",
    example: "111111111111. Leave it empty if you do not know it yet.",
    article: "installations",
  },
  "customers.awsRegion": {
    title: "AWS region",
    body: "The AWS region the proxy stack runs in. Optional; it is for your records.",
    example: "us-east-1 for US East (N. Virginia).",
    article: "installations",
  },
  "customers.secretRotation": {
    title: "Secret",
    body: "The installation's secret. Rotate issues a second secret; both work until you promote it, which retires the old one. Revoke stops both at once.",
    example: "Rotate, store the new secret in the stack's Secrets Manager entry, redeploy, then Promote.",
    article: "installations",
    section: "secrets",
  },
  "customers.secretCopied": {
    title: "I have copied this secret",
    body: "The secret is shown only once. Tick this after storing it (for an installation, in the stack's Secrets Manager entry) to close the dialog.",
    example: "Copy nxi_… into the installation_secret_arn secret, then tick and close.",
    article: "installations",
    section: "secrets",
  },
  "customers.awsSignIn": {
    title: "AWS sign-in",
    body: "Developers call the proxy with the AWS credentials they already have, through nexus-aws-token. No key is issued. Only principals of an allowed account that match an allowed pattern get in.",
    example: "A developer runs aws sso login, then Claude Code; the proxy checks their Identity Center role in the installation's account.",
    article: "users-teams-and-sign-in",
    section: "aws-sign-in",
  },
  "customers.awsAllowedAccounts": {
    title: "Additional allowed accounts",
    body: "Further AWS accounts whose principals may sign in, one 12-digit id per line (at most 100). Each installation's own account is always allowed and need not be listed.",
    example: "Add 333333333333 so developers of the customer's sandbox account can reach the production proxy.",
    article: "users-teams-and-sign-in",
    section: "aws-sign-in",
  },
  "customers.awsAllowedPrincipals": {
    title: "Allowed principals",
    body: "Patterns a caller must match, one per line (at most 100): role/…, permission-set/…, user/… or federated-user/…, with * as a wildcard. permission-set/… matches Identity Center roles in the installation's own account only; in other accounts use role/… patterns. An empty list admits every principal of an allowed account, service roles included.",
    example: "permission-set/Developers admits Identity Center users of that permission set; role/ci-* adds CI roles.",
    article: "users-teams-and-sign-in",
    section: "aws-sign-in",
  },
  "customers.awsTeamMappings": {
    title: "Team mappings",
    body: "Which team a caller's usage lands in, by principal pattern. The first matching row wins; a caller who matches none has no team. At most 200.",
    example: "permission-set/DataScience → data, then permission-set/* → engineering.",
    article: "users-teams-and-sign-in",
    section: "aws-sign-in",
  },
  "customers.awsMappingPattern": {
    title: "Principal pattern",
    body: "The principal pattern this mapping matches, in the same form as the allowed principals.",
    example: "role/ci-* matches every role whose name starts with ci-.",
    article: "users-teams-and-sign-in",
    section: "aws-sign-in",
  },
  "customers.oidcProviders": {
    title: "OIDC providers",
    body: "Identity providers whose tokens the proxy accepts in its oidc sign-in mode, for this customer. The issuer must be https and reachable when you add it.",
    example: "https://login.hooli.com with audience nexus-proxy lets Hooli's SSO tokens call the proxy.",
    article: "users-teams-and-sign-in",
    section: "oidc",
  },
  "customers.oidcIssuer": {
    title: "Issuer",
    body: "The provider's issuer URL. Nexus fetches its discovery document and keys once, when you add it, and refuses one it cannot reach or that resolves to a private address.",
    example: "https://login.example.com",
    article: "users-teams-and-sign-in",
    section: "oidc",
  },
  "customers.oidcAudience": {
    title: "Audience",
    body: "The aud claim the provider puts in tokens meant for the proxy. Tokens with another audience are refused.",
    example: "nexus-proxy",
    article: "users-teams-and-sign-in",
    section: "oidc",
  },
  "customers.oidcJwks": {
    title: "JWKS URI (optional)",
    body: "Where the provider publishes its signing keys, if not the one its discovery document names. Usually left empty.",
    example: "https://login.example.com/.well-known/jwks.json",
    article: "users-teams-and-sign-in",
    section: "oidc",
  },
  "customers.apiKeys": {
    title: "API keys",
    body: "Keys for developers who cannot use AWS sign-in (user keys, nxk_…) and for integrations (service keys, nxs_…). Every key expires within 365 days; the secret is shown once.",
    example: "A contractor without AWS access gets a user key that expires at the end of the engagement.",
    article: "users-teams-and-sign-in",
    section: "api-keys",
  },
  "customers.keyKind": {
    title: "Kind",
    body: "User: one developer's proxy key, what goes in ANTHROPIC_AUTH_TOKEN. Service: a key for an integration, with scopes.",
    example: "A reporting job gets a service key with reports:read.",
    article: "users-teams-and-sign-in",
    section: "api-keys",
  },
  "customers.keyPrefix": {
    title: "Key",
    body: "The start of the key, enough to recognise it. The full secret is never shown again after it is issued.",
    example: "nxk_7f3a… is the key a developer has in their Claude Code settings.",
    article: "users-teams-and-sign-in",
    section: "api-keys",
  },
  "customers.keyUser": {
    title: "User",
    body: "The developer the user key belongs to: their usage is recorded under them. Only active users are listed; a user appears after their first request, or an admin adds them.",
    example: "Choose “Dana Lee (dana@hooli.com)” to issue Dana's key for Claude Code.",
    article: "users-teams-and-sign-in",
    section: "api-keys",
  },
  "customers.keyScopes": {
    title: "Scopes",
    body: "What a service key may do: reports:read reads usage and statements, prompts:read reads prompt logs, admin:write changes the customer's settings.",
    example: "A billing export needs only reports:read.",
    article: "users-teams-and-sign-in",
    section: "api-keys",
  },
  "customers.keyExpiry": {
    title: "Expires on",
    body: "The day the key stops working. Required, in the future, and at most 365 days ahead — no key lives forever.",
    example: "Issued on 25 Sep 2026, a key can expire on 25 Sep 2027 at the latest.",
    article: "users-teams-and-sign-in",
    section: "api-keys",
  },
  "customers.keyLimit": {
    title: "Keys per person",
    body: "How many live keys one person may hold on the proxy, one per device (1 to 50). At the limit a new sign-in is refused until the person revokes a key on the proxy's /keys page. Admin-issued keys do not count.",
    example: "10, the default, covers a laptop, a desktop and a few CI runners.",
    article: "users-teams-and-sign-in",
    section: "proxy-sign-in",
  },
  "customers.trustedTokens": {
    title: "Trusted tokens",
    body: "Third-party JWT issuers whose tokens the proxy accepts, at most 20. A trusted token is never a button on the sign-in page. Nothing secret is stored: the proxy checks each token against the issuer's public keys.",
    example: "GitHub Actions' issuer, so a CI job can trade its token for a machine key.",
    article: "users-teams-and-sign-in",
    section: "proxy-sign-in",
  },
  "customers.tokenIssuer": {
    title: "Issuer",
    body: "Compared exactly with the token's iss claim, after lower-casing the scheme and host and dropping a trailing slash. https only, no query or fragment.",
    example: "https://token.actions.githubusercontent.com",
    article: "users-teams-and-sign-in",
    section: "proxy-sign-in",
  },
  "customers.tokenJwks": {
    title: "JWKS URL",
    body: "Where the issuer publishes its public signing keys. https only; a redirect to another host is refused.",
    example: "https://token.actions.githubusercontent.com/.well-known/jwks",
    article: "users-teams-and-sign-in",
    section: "proxy-sign-in",
  },
  "customers.tokenAudiences": {
    title: "Audiences",
    body: "The token's aud must be one of these, one per line (1 to 20).",
    example: "nexus, the audience a CI job asks its issuer for.",
    article: "users-teams-and-sign-in",
    section: "proxy-sign-in",
  },
  "customers.tokenAlgorithms": {
    title: "Algorithms",
    body: "The signature algorithms accepted. Asymmetric only: none and the HMAC algorithms can never be allowed.",
    example: "RS256 for most issuers; ES256 for some cloud providers.",
    article: "users-teams-and-sign-in",
    section: "proxy-sign-in",
  },
  "customers.tokenClaims": {
    title: "Claims",
    body: "Which claims carry the subject, the email and the groups. Leave email or groups empty when the issuer sends none. A token with a verified email signs in as the SSO person with that email.",
    example: "Subject sub, email empty, groups empty for a CI issuer.",
    article: "users-teams-and-sign-in",
    section: "proxy-sign-in",
  },
  "customers.tokenSubjects": {
    title: "Allowed subjects",
    body: "When not empty, only these subjects may sign in, one per line; * is a wildcard.",
    example: "repo:acme/* admits every repository of the acme organisation.",
    article: "users-teams-and-sign-in",
    section: "proxy-sign-in",
  },
  "customers.tokenLife": {
    title: "Longest token life (seconds)",
    body: "A token whose expiry is further than this after it was issued, or after now, is refused. 60 to 86,400.",
    example: "3600, the default: one hour.",
    article: "users-teams-and-sign-in",
    section: "proxy-sign-in",
  },
  "customers.tokenMode": {
    title: "How the token is used",
    body: "Exchange (the default): the proxy trades the token once for a revocable machine key. Bearer: the token itself is sent on model requests. Either allows both.",
    example: "Exchange for CI, so a leaked job token cannot be replayed against the models.",
    article: "users-teams-and-sign-in",
    section: "proxy-sign-in",
  },
  "customers.tokenInternal": {
    title: "Internal issuer",
    body: "Allow the issuer to resolve to a private address. Otherwise private, link-local and metadata addresses are refused.",
    example: "On for a self-hosted identity provider inside the customer's VPC.",
    article: "users-teams-and-sign-in",
    section: "proxy-sign-in",
  },
  "customers.retention": {
    title: "Prompt logging and retention",
    body: "Whether prompt bodies are stored, and how long prompt bodies and raw usage rows are kept. Monthly usage totals are kept forever.",
    example: "Logging on, prompts kept 30 days, raw usage kept 400 days.",
    article: "adding-a-customer",
    section: "settings",
  },
  "customers.promptLogging": {
    title: "Prompt logging enabled",
    body: "The customer-wide switch for storing prompt bodies. Off discards them at ingest, so nothing is ever written. The installation's own prompt_logging setting must also be on.",
    example: "Turn it off for a customer whose contract forbids storing prompts.",
    article: "adding-a-customer",
    section: "settings",
  },
  "customers.assistantEnabled": {
    title: "Assistant on the customer's proxy",
    body: "Whether the customer's admins have the Assistant in their proxy's Admin area. On by default; off hides it there, and AllCode's own Assistant conversations about the customer are unaffected. Only a super-admin can change it.",
    example: "Turn it off for a customer who has asked not to have the Assistant.",
    article: "adding-a-customer",
    section: "settings",
  },
  "customers.promptRetention": {
    title: "Prompt retention (days)",
    body: "How long stored prompt bodies are kept before the hourly retention job deletes them. 1 to 3,650 days.",
    example: "30 keeps a month of prompts for debugging.",
    article: "adding-a-customer",
    section: "settings",
  },
  "customers.usageRetention": {
    title: "Usage retention (days)",
    body: "How long raw usage-event rows are kept. 30 to 3,650 days. Monthly totals and invoices are kept regardless.",
    example: "400 keeps a year of raw events for disputes.",
    article: "adding-a-customer",
    section: "settings",
  },
  // The library Teams screen (@nexus/ui, TASK-578) names these; the proxy shell registers the same ids.
  "teams.list": {
    title: "Teams",
    body: "Groups of users for the usage reports. A team comes from the team_id claim of a token, an AWS sign-in team mapping, or an admin creating it here. Only a team with no members can be deleted.",
    example: "Creating “Platform” here, then mapping permission-set/Developers to its team id, puts every developer of that permission set in Platform.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "teams.name": {
    title: "Name",
    body: "What the team is called in reports and on screens. 1 to 80 characters; it can be changed at any time without affecting who is in the team.",
    example: "“Platform” or “Data science”.",
    article: "users-teams-and-sign-in",
    section: "users-and-teams",
  },
  "teams.externalId": {
    title: "Team id",
    body: "The team's id as the proxy sees it: the team_id claim value, or the id a team mapping names. Two teams of one customer cannot share an id, and it cannot be changed once the team exists.",
    example: "A mapping to team id “platform” lands callers in the team whose id is platform.",
    article: "users-teams-and-sign-in",
    section: "aws-sign-in",
  },
} satisfies Record<string, HelpTopic>;
